import type { CampaignStatus } from '~/types/graphql';
import type { IDisableReasonEntry } from '../types';

import { CampaignHelpText } from '~/modules/campaign/components/CampaignPickItem';
import { getLabelByTemplateType, genTextByTotal } from '~/utils';
import { TemplateType } from '~/types/template';
import { useTranslation } from 'react-i18next';
import { IDisableReasonKey } from '../types';
import { useMemo } from 'react';

export interface ICampaignHelpTextParams {
  campaignStatus?: CampaignStatus;
  campaignName?: string;
}

interface IUseDataTemplateDisabledResult {
  dataTemplateDisabled: Record<IDisableReasonKey, IDisableReasonEntry>;
}

export const useDataTemplateDisabled = (): IUseDataTemplateDisabledResult => {
  const { t } = useTranslation();

  const dataTemplateDisabled = useMemo<Record<IDisableReasonKey, IDisableReasonEntry>>(() => {
    return {
      [IDisableReasonKey.IS_USED_FOR_CONTROL]: {
        key: IDisableReasonKey.IS_USED_FOR_CONTROL,
        content: () => t('This template is used for Control version'),
      },
      [IDisableReasonKey.IS_USED_FOR_VARIANT]: {
        key: IDisableReasonKey.IS_USED_FOR_VARIANT,
        content: () => t('This template is used for Variant version'),
      },
      [IDisableReasonKey.IS_SELECTED]: {
        key: IDisableReasonKey.IS_SELECTED,
        content: () => t('This template is selected'),
      },
      [IDisableReasonKey.IS_NO_ASSIGNED_BY_GEMPAGES]: {
        key: IDisableReasonKey.IS_NO_ASSIGNED_BY_GEMPAGES,
        hidePageAssigned: true,
        isHasShortcut: true,
        content: ({ templateType }: { templateType: TemplateType }) => {
          if (templateType === TemplateType.HOME) {
            return t('Make this template as default to use it');
          }

          return t('Template needs at least one {{templateType}} assigned to work', {
            templateType: getLabelByTemplateType(templateType),
          });
        },
      },
      [IDisableReasonKey.IS_NO_ASSIGNED_BY_SHOPIFY]: {
        key: IDisableReasonKey.IS_NO_ASSIGNED_BY_SHOPIFY,
        hidePageAssigned: true,
        isHasShortcut: true,
        content: ({ templateType }: { templateType: TemplateType }) => {
          if (templateType === TemplateType.HOME) {
            return t('Make this template as default to use it');
          }

          return t('Template needs at least one {{templateType}} assigned to work', {
            templateType: getLabelByTemplateType(templateType),
          });
        },
      },
      [IDisableReasonKey.IS_TEMPLATE_DRAFT]: {
        key: IDisableReasonKey.IS_TEMPLATE_DRAFT,
        content: () => t('Template needs to be published to work'),
      },
      [IDisableReasonKey.IS_TEMPLATE_USED_TO_TEST]: {
        key: IDisableReasonKey.IS_TEMPLATE_USED_TO_TEST,
        content: ({
          assignedCount,
          assignedTotal,
          templateType,
        }: {
          assignedCount: number;
          assignedTotal: number;
          templateType: string;
        }) => {
          const pluralizedType = genTextByTotal(templateType, assignedTotal);
          return t(
            'This template is used to test for {{assignedCount}}/{{assignedTotal}} {{pluralizedType}}',
            {
              assignedCount,
              assignedTotal,
              pluralizedType,
            },
          );
        },
      },
      [IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN]: {
        key: IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN,
        content: (params: ICampaignHelpTextParams) => <CampaignHelpText {...params} />,
        isHasShortcut: true,
      },
      [IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP]: {
        key: IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP,
        content: (params: ICampaignHelpTextParams) => <CampaignHelpText {...params} />,
        isHasShortcut: true,
      },
      [IDisableReasonKey.IS_TEMPLATE_VARIANT_NOT_ASSIGNED]: {
        key: IDisableReasonKey.IS_TEMPLATE_VARIANT_NOT_ASSIGNED,
        content: () => t('This template is not same page type with Variant'),
      },
    };
  }, [t]);

  return { dataTemplateDisabled };
};
